import { Card, Heading, Link, Span } from "@icg360/design-system";

import { BaseLayout } from "components/base-layout";
import { Stack } from "components/common/stack";
import { MainLayout } from "components/shared/main-layout";

import styles from "./laundry-room-blog.module.scss";

export const LaundryRoomBlog = () => {
  return (
    <BaseLayout>
      <MainLayout className="">
        <Card className={styles.contentCard}>
          <div className={styles.headerImage}>
            <div className={styles.headerCopy}>
              <Heading size="md">Room by room:</Heading>
              <div className={styles.secondaryHeader}>
                <Heading size="xl">
                  Preventing damage in the laundry area
                </Heading>
              </div>
            </div>
          </div>

          <div className={styles.content}>
            <Stack gap="2xl">
              <div>
                <Span size="lg">
                  The laundry room is one of the hardest working areas of the
                  home, but it can be a hidden source of water damage and fire
                  risk. According to the Insurance Information Institute
                  <sup>1</sup>, almost{" "}
                  <b>24% of property claims involve water</b>, and the average
                  cost of a water damage claim is $13,954.
                </Span>
                <br></br>
              </div>
              <div>
                <Span size="lg">
                  The good news? A little maintenance goes a long way. Here are
                  three simple steps homeowners can take to reduce the risk of
                  damage and costly claims in the laundry room:
                </Span>
              </div>
              <div className={styles.imageWithCopy}>
                <img
                  alt="Washing machines hoses connected to the wall"
                  className={styles.mobileLeftImg}
                  src="https://externalmedia-ss-prod.s3.us-east-1.amazonaws.com/sagesure/images/policyholder-offers/affiliate-blog/affiliate-laundry-image1.png"
                />
                <div className={styles.bodyCopy}>
                  <Heading className={styles.bodyHeading} size="sm">
                    Regularly check the washer hoses
                  </Heading>
                  <Span size="lg">
                    Rubber hoses can crack, bulge, or loosen over time with use
                    and age. It’s important to check them every few months for
                    signs of wear, especially near the connections. Consider
                    replacing rubber hoses with{" "}
                    <Link
                      href="https://www.homedepot.com/p/Everbilt-3-4-in-FHT-x-3-4-in-FHT-x-96-in-Stainless-Steel-Washing-Machine-Supply-Line-7243-96-34-1-EB/205854150"
                      target="_blank"
                    >
                      stainless steel braided ones
                    </Link>
                    , which are more durable and less likely to burst.
                  </Span>
                </div>
                <img
                  alt="Washing machines hoses connected to the wall"
                  className={styles.rightImg}
                  src="https://externalmedia-ss-prod.s3.us-east-1.amazonaws.com/sagesure/images/policyholder-offers/affiliate-blog/affiliate-laundry-image1.png"
                />
              </div>
              <div className={styles.imageWithCopy}>
                <img
                  alt="Puddle of water on a laundry room floor"
                  className={styles.leftImg}
                  src="https://externalmedia-ss-prod.s3.us-east-1.amazonaws.com/sagesure/images/policyholder-offers/affiliate-blog/affiliate-laundry-image2.png"
                />
                <div className={styles.bodyCopy}>
                  <Heading className={styles.bodyHeading} size="sm">
                    Install a leak detector
                  </Heading>
                  <Span size="lg">
                    <Link
                      href="https://www.homedepot.com/p/Zircon-Leak-Alert-Wi-Fi-Electronic-Water-Detector-68882/305673694"
                      target="_blank"
                    >
                      Smart water leak sensors
                    </Link>{" "}
                    can send an alert to your phone if water is detected under
                    the washer or beneath the laundry room sink. Early detection
                    can mean the difference between a quick fix and costly
                    damage.
                  </Span>
                </div>
              </div>
              <div className={styles.imageWithCopy}>
                <img
                  alt="A dirty dryer vent with a cleaning brush in it"
                  className={styles.mobileLeftImg}
                  src="https://externalmedia-ss-prod.s3.us-east-1.amazonaws.com/sagesure/images/policyholder-offers/affiliate-blog/affiliate-laundry-image3.png"
                />
                <div className={styles.bodyCopy}>
                  <Heading className={styles.bodyHeading} size="sm">
                    Clean and maintain the dryer vent
                  </Heading>
                  <Span size="lg">
                    Water isn’t the only culprit of damage in a laundry room.
                    Lint buildup in dryers is a major hazard, responsible for
                    roughly 26% of dryer fires<sup>2</sup>. Clean the lint trap
                    after every load, and make sure to clean the dryer vent once
                    a year. You can DIY this project with a{" "}
                    <Link
                      href="https://www.homedepot.com/p/Gardus-LintEater-Rotary-Dryer-Vent-Cleaning-System-RLE202/206714870"
                      target="_blank"
                    >
                      dryer vent cleaning system
                    </Link>
                    , or you can hire a professional.
                  </Span>
                </div>
                <img
                  alt="A dirty dryer vent with a cleaning brush in it"
                  className={styles.rightImg}
                  src="https://externalmedia-ss-prod.s3.us-east-1.amazonaws.com/sagesure/images/policyholder-offers/affiliate-blog/affiliate-laundry-image3.png"
                />
              </div>
              <div className={styles.bottomCopy}>
                <Span size="lg">
                  Taking a few proactive steps can keep your laundry room clean,
                  safe, and free from costly damage.
                </Span>
              </div>
              <div className={styles.footerCopy}>
                <Span size="sm" color="quiet">
                  <div>
                    <sup>1</sup>{" "}
                    https://www.iii.org/fact-statistic/facts-statistics-homeowners-and-renters-insurance#Causes%20of%20homeowners%20insurance%20losses
                  </div>
                  <div>
                    <sup>2</sup>{" "}
                    https://www.nfpa.org/education-and-research/research/nfpa-research/fire-statistical-reports/home-fires-involving-clothes-dryers-and-washing-machines
                  </div>
                  <br />
                  <div>
                    This information is designed to help customers better
                    understand ways to reduce the risk of property losses and
                    improve protection. The tips, tools, and resources shared,
                    including links to third-party products or services, are
                    provided for informational purposes only. SageSure does not
                    receive any commission, fees, or financial incentives from
                    the inclusion of product links or references. SageSure does
                    not guarantee the performance, reliability, or suitability
                    of any referenced third-party product or service. All
                    external links are provided solely to illustrate potential
                    options. You are encouraged to speak with your agent or a
                    licensed professional before making decisions based on the
                    information provided. Our goal is to empower you with
                    knowledge. Use of any third-party resources is at your own
                    discretion and risk.
                  </div>
                </Span>
              </div>
            </Stack>
          </div>
        </Card>
      </MainLayout>
    </BaseLayout>
  );
};
